/**
 * 对话框管理器基类
 * 提供对话框的通用生命周期管理
 */
import { BaseManager } from './base-manager';
import { ManagerRegistry } from './manager-registry';
import { BimDialog } from '../components/dialog';

/** 对话框配置选项 */
export interface DialogManagerOptions {
    /** 是否可拖拽 */
    draggable?: boolean;
    /** 是否可调整大小 */
    resizable?: boolean;
}

/**
 * 对话框管理器抽象基类
 * 子类需要实现 dialogId、dialogTitle 和 createContent 方法
 */
export abstract class BaseDialogManager extends BaseManager {
    /** 对话框实例 */
    protected dialog: BimDialog | null = null;
    /** 对话框是否可见 */
    protected isVisible: boolean = false;

    constructor(registry: ManagerRegistry) {
        super(registry);
    }

    /** 对话框唯一标识（子类必须实现） */
    protected abstract get dialogId(): string;
    /** 对话框标题，支持国际化 key（子类必须实现） */
    protected abstract get dialogTitle(): string;

    /** 对话框宽度，默认 300 */
    protected get dialogWidth(): number {
        return 300;
    }

    /** 对话框高度，默认 'auto' 自适应 */
    protected get dialogHeight(): number | 'auto' {
        return 'auto';
    }

    /** 对话框选项，默认可拖拽不可缩放 */
    protected get dialogOptions(): DialogManagerOptions {
        return { draggable: true, resizable: false };
    }

    /**
     * 自定义标题栏内容（可选）。
     * 返回 HTMLElement 时将完全替换默认标题栏（title + close 按钮）。
     * 适用于需要在标题栏插入 tab、图标、图片等自定义布局的场景。
     * 与 dialogTitle 互斥，优先使用此值。
     */
    protected get dialogHeader(): HTMLElement | undefined {
        return undefined;
    }

    /** 是否隐藏关闭按钮（默认 false） */
    protected get dialogHideCloseButton(): boolean {
        return false;
    }

    /** 创建对话框内容（子类必须实现） */
    protected abstract createContent(): HTMLElement;

    /** 对话框关闭时的回调，子类可重写 */
    protected onDialogClose(): void {}
    /** 对话框创建后的回调，子类可重写 */
    protected onDialogCreated(): void {}
    /** 销毁前的回调，子类可重写 */
    protected onBeforeDestroy(): void {}

    /**
     * 获取对话框位置
     * 默认定位在容器右侧居中，子类可重写
     */
    protected getDialogPosition(): { x: number; y: number } {
        const container = this.registry.container;
        if (!container) return { x: 100, y: 100 };

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const width = this.dialogWidth;
        const height = typeof this.dialogHeight === 'number' ? this.dialogHeight : 300;
        const paddingRight = 20;

        return {
            x: containerWidth - width - paddingRight,
            y: (containerHeight - height) / 2
        };
    }

    /** 显示对话框 */
    public show(): void {
        if (!this.registry.dialog || !this.registry.container) {
            console.warn(`[${this.dialogId}] Dialog manager or container is not initialized`);
            return;
        }

        if (this.dialog) {
            this.destroyDialog();
        }

        const position = this.getDialogPosition();
        const content = this.createContent();
        const options = this.dialogOptions;

        this.dialog = this.registry.dialog.create({
            id: this.dialogId,
            title: this.dialogTitle,
            content,
            width: this.dialogWidth,
            height: this.dialogHeight,
            position,
            draggable: options.draggable,
            resizable: options.resizable,
            header: this.dialogHeader,
            hideCloseButton: this.dialogHideCloseButton,
            onClose: () => {
                this.onDialogClose();
                this.destroyDialog();
            }
        });
        this.dialog.init();
        this.isVisible = true;
        this.onDialogCreated();
    }

    /** 隐藏对话框 */
    public hide(): void {
        this.destroyDialog();
    }

    /** 切换对话框显示状态 */
    public toggle(): void {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /** 获取对话框是否打开 */
    public isOpen(): boolean {
        return this.isVisible;
    }

    /** 销毁对话框 */
    protected destroyDialog(): void {
        this.onBeforeDestroy();
        if (this.dialog) {
            this.dialog.destroy();
            this.dialog = null;
        }
        this.isVisible = false;
    }

    /** 销毁管理器 */
    public destroy(): void {
        this.destroyDialog();
        super.destroy();
    }
}
